import React, { FC, memo, useCallback, useState } from 'react';
import { createUseStyles } from 'src/theming';
import { MdClose as CloseIcon } from '@react-icons/all-files/md/MdClose';
import { MdAttachFile as FileIcon } from '@react-icons/all-files/md/MdAttachFile';
import { ApiReturnType } from '../../../../interfaces/api/api';

const useStyles = createUseStyles((theme) => ({
  root: {
    position: 'relative',
    marginRight: 10,
    marginBottom: 10,
    '& img': {
      width: 80,
      height: 80,
      borderRadius: 10,
      background: theme.palette.primary.main,
      objectFit: 'cover',
    },
  },
  deleteButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '50%',
    top: 6,
    right: 6,
    padding: 2,
    position: 'absolute',
    backgroundColor: '#161c24b8',
    color: 'white',
    cursor: 'pointer',
  },
  emptyContainer: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
    padding: 10,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    color: 'white',
  },
  filename: {
    textOverflow: 'ellipsis',
    width: '100%',
    overflow: 'hidden',
  },
}));

interface Props {
  itemId?: string;
  onDelete: () => void;
  file: File & { id?: string };
  getItemAttachmentApi?: (attachmentId: string) => ApiReturnType;
}

const AttachmentFile: FC<Props> = ({ file, onDelete, getItemAttachmentApi, itemId }) => {
  const [hasPreview, setHasPreview] = useState(true);
  const classes = useStyles();

  const getAttachmentUrl = useCallback(
    (attachmentId: string): string => {
      if (getItemAttachmentApi && itemId) {
        return getItemAttachmentApi(attachmentId).endpoint;
      }

      return '';
    },
    [itemId, getItemAttachmentApi]
  );

  const getFilePreview = useCallback(() => {
    if (!file.id) {
      return URL.createObjectURL(file);
    } else {
      return getAttachmentUrl(file.id);
    }
  }, [file]);

  const handleAttachmentClick = () => {
    if (file.id) {
      const endpoint = getAttachmentUrl(file.id);
      window.open(endpoint, '_blank');
    }
  };

  return (
    <div className={classes.root}>
      <img src={getFilePreview()} alt='' onError={() => setHasPreview(false)} onClick={handleAttachmentClick} />

      {!hasPreview && (
        <div className={classes.emptyContainer}>
          <FileIcon size={18} />
          <div className={classes.filename}>{file.name}</div>
        </div>
      )}

      {!file.id && (
        <div className={classes.deleteButton} onClick={onDelete}>
          <CloseIcon />
        </div>
      )}
    </div>
  );
};

export default memo(AttachmentFile);
